import { useMemo, useState } from 'react';
import { sortHand, type HazariLegal, type HazariView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

export function HazariTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as HazariView;
  const legal = useGame((s) => s.legal) as HazariLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.cards), [view.cards]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const handEnd = view.phase === 'HAND_END' && view.handResults.length > 0 ? view.handResults.at(-1)! : null;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.totals[s] ?? 0} {t('hz.pts')}</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {(view.pointsTaken[s] ?? 0) > 0 && <span className="tag blind">+{view.pointsTaken[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">{t('hz.hand', { n: view.hand })}</div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.hand}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'TRICK_END' && view.lastTrick && view.lastTrick.points > 0 && (
            <div className="center-note" role="status">
              {t('hz.won', { name: name(view.lastTrick.winner), n: view.lastTrick.points })}
            </div>
          )}
          {view.phase === 'PLAYING' && view.trick.length === 0 && myTurn && <div className="center-note" role="status">{t('hz.leadHint')}</div>}
          {handEnd && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('hz.handScores', { n: view.hand })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {handEnd.map((sc, s) => (
                  <span key={s} className={`pill ${sc === Math.max(...handEnd) ? 'accent' : ''}`}>
                    {name(s)}: {sc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.totals[mySeat] ?? 0} {t('hz.pts')}{(view.pointsTaken[mySeat] ?? 0) > 0 ? ` · +${view.pointsTaken[mySeat]}` : ''}</> : null} />
      </div>
      <div className="table-bottom">
        <Hand size="lg" label="Your hand">
          {hand.map((c) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 70 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>H{r + 1}</td>
                {row.map((v, s) => (
                  <td key={s} className="num">{v}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('hz.scoreHint', { target: view.config.targetScore, max: view.config.maxHands })}</p>
      </Modal>
    </>
  );
}
